import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, ArrowUpRight, CalendarDays } from 'lucide-react';

export const formatBlogDate = (value) => {
  if (!value) return '';
  const d = new Date(String(value).replace(' ', 'T'));
  if (isNaN(d)) return '';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Neutral fallback so a post published without a cover still renders a complete card.
const FALLBACK_COVER =
  'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&q=80';

export function AuthorLine({ post, className = '' }) {
  if (!post.author_name) return null;
  const initials = post.author_name.trim().charAt(0).toUpperCase();

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {post.author_image ? (
        <img
          src={post.author_image}
          alt={post.author_name}
          className="w-7 h-7 rounded-full object-cover border"
          loading="lazy"
        />
      ) : (
        <span className="w-7 h-7 rounded-full bg-primary/15 text-primary text-xs font-bold flex items-center justify-center">
          {initials}
        </span>
      )}
      <span className="text-xs text-muted-foreground truncate">
        <span className="text-foreground/80 font-medium">{post.author_name}</span>
        {post.author_role ? ` · ${post.author_role}` : ''}
      </span>
    </div>
  );
}

export default function BlogCard({ post, index = 0 }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(index, 6) * 0.06 }}
      className="h-full"
    >
      <Link
        to={`/blog/${post.slug}`}
        className="group flex h-full flex-col overflow-hidden rounded-2xl border bg-card transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-primary/40"
      >
        <div className="relative aspect-[16/9] overflow-hidden bg-muted">
          <img
            src={post.cover_image || FALLBACK_COVER}
            alt={post.title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={(e) => { e.currentTarget.src = FALLBACK_COVER; }}
          />
          {post.category && (
            <span className="absolute left-3 top-3 rounded-full bg-background/90 px-2.5 py-1 text-[11px] font-semibold text-foreground backdrop-blur-sm">
              {post.category}
            </span>
          )}
        </div>

        <div className="flex flex-1 flex-col p-5">
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
            {post.published_at && (
              <span className="flex items-center gap-1">
                <CalendarDays className="h-3 w-3" /> {formatBlogDate(post.published_at)}
              </span>
            )}
            {post.read_time && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" /> {post.read_time}
              </span>
            )}
          </div>

          <h3 className="mt-2 font-heading text-lg font-bold leading-snug text-foreground line-clamp-2 transition-colors group-hover:text-primary">
            {post.title}
          </h3>

          {post.excerpt && (
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground line-clamp-3">
              {post.excerpt}
            </p>
          )}

          <div className="mt-auto flex items-center justify-between gap-3 pt-4">
            <AuthorLine post={post} className="min-w-0" />
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-muted-foreground transition-all group-hover:border-primary group-hover:bg-primary group-hover:text-primary-foreground">
              <ArrowUpRight className="h-4 w-4" />
            </span>
          </div>
        </div>
      </Link>
    </motion.article>
  );
}
